import {
	DragEndEvent,
	DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { WidgetConfig, WidgetRow } from 'components/farm-dashboard/types'
import { useCallback, useMemo, useState } from 'react'
import { closeWidgetDock } from 'store/dashboardSlice'
import { useAppDispatch } from 'store/hooks'
import { getWidgetDimensions } from '../constants/constants'
import {
	addWidgetToRow,
	createEmptyRow,
	findWidgetInRows,
	insertRowWithWidget,
	moveWidgetBetweenRows,
	removeEmptyRows,
	reorderWidgetsInRow,
} from '../utils/widgetRowUtils'

type Params = {
	rows: WidgetRow[]
	onUpdateRows: (rows: WidgetRow[]) => void
}

export function useDashboardDnd({ rows, onUpdateRows }: Params) {
	const dispatch = useAppDispatch()
	const [activeId, setActiveId] = useState<string | null>(null)
	const [activeDockWidget, setActiveDockWidget] = useState<WidgetConfig | null>(
		null,
	)

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor),
	)

	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			const activeIdStr = event.active.id as string
			setActiveId(activeIdStr)
			dispatch(closeWidgetDock())
			const data = event.active.data?.current ?? {}
			if (data.from === 'dock' && data.widget) {
				setActiveDockWidget(data.widget as WidgetConfig)
			}
		},
		[dispatch],
	)

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event

			setActiveId(null)
			setActiveDockWidget(null)

			if (!over) return

			const fromDock = active?.data?.current?.from === 'dock'
			const draggableWidget: WidgetConfig | undefined =
				active?.data?.current?.widget

			// Обработка добавления виджета из dock'а
			if (fromDock && draggableWidget) {
				const allWidgets = rows.flatMap(r => r.widgets)
				const exists = allWidgets.some(w => w.type === draggableWidget.type)
				if (exists) return

				const widgetDimensions = getWidgetDimensions(draggableWidget.size)
				const isFullWidget = widgetDimensions.w === -1

				const overData = over.data?.current
				let targetRowId: string | null = null

				if (overData?.type === 'row-insert') {
					const insertAfterRowId = overData.insertAfterRowId as string | null
					const updatedRows = insertRowWithWidget(
						rows,
						insertAfterRowId,
						draggableWidget,
					)
					onUpdateRows(updatedRows)
					return
				}

				// FULL виджеты всегда создают новую строку
				if (isFullWidget) {
					if (overData?.type === 'row' || overData?.type === 'widget') {
						const insertAfterRowId = overData.rowId as string
						const updatedRows = insertRowWithWidget(
							rows,
							insertAfterRowId,
							draggableWidget,
						)
						onUpdateRows(updatedRows)
						return
					}

					const updatedRows = insertRowWithWidget(
						rows,
						rows.length > 0 ? rows[rows.length - 1].id : null,
						draggableWidget,
					)
					onUpdateRows(updatedRows)
					return
				}

				// Для не-FULL виджетов добавляем в существующую строку
				if (overData?.type === 'row') {
					targetRowId = overData.rowId
				} else if (overData?.type === 'widget') {
					// Упал на виджет - берем его строку
					targetRowId = overData.rowId
				} else if (over.id === 'dashboard-dropzone') {
					if (rows.length > 0) {
						targetRowId = rows[rows.length - 1].id
					} else {
						const newRow = createEmptyRow(0)
						targetRowId = newRow.id
						const updatedRows = [...rows, newRow]
						onUpdateRows(
							addWidgetToRow(updatedRows, targetRowId, draggableWidget),
						)
						return
					}
				}

				if (targetRowId) {
					const updatedRows = addWidgetToRow(rows, targetRowId, draggableWidget)
					onUpdateRows(updatedRows)
				}
				return
			}

			// Обработка перемещения существующего виджета
			const activeWidgetInfo = findWidgetInRows(active.id as string, rows)
			if (!activeWidgetInfo) return

			const activeWidget = activeWidgetInfo.widget
			const activeWidgetDimensions = getWidgetDimensions(activeWidget.size)
			const isActiveFullWidget = activeWidgetDimensions.w === -1

			const overData = over.data?.current
			const overRowId = overData?.rowId

			// Обработка вставки виджета между строками
			if (overData?.type === 'row-insert') {
				const insertAfterRowId = overData.insertAfterRowId as string | null
				const rowsWithoutWidget = rows.map(row => {
					if (row.id === activeWidgetInfo.rowId) {
						return {
							...row,
							widgets: row.widgets.filter(w => w.id !== active.id),
						}
					}
					return row
				})
				const updatedRows = insertRowWithWidget(
					rowsWithoutWidget,
					insertAfterRowId,
					activeWidget,
				)
				onUpdateRows(removeEmptyRows(updatedRows))
				return
			}

			// Если упал на строку или виджет в другой строке
			if (overData?.type === 'row' || overData?.type === 'widget') {
				if (overRowId && overRowId !== activeWidgetInfo.rowId) {
					if (isActiveFullWidget) {
						const rowsWithoutWidget = rows.map(row => {
							if (row.id === activeWidgetInfo.rowId) {
								return {
									...row,
									widgets: row.widgets.filter(w => w.id !== active.id),
								}
							}
							return row
						})
						const updatedRows = insertRowWithWidget(
							rowsWithoutWidget,
							overRowId,
							activeWidget,
						)
						onUpdateRows(removeEmptyRows(updatedRows))
						return
					}

					// Перемещение между строками для не-FULL виджетов
					const updatedRows = moveWidgetBetweenRows(
						rows,
						activeWidgetInfo.rowId,
						overRowId,
						active.id as string,
					)
					onUpdateRows(updatedRows)
					return
				}
			}

			// Перемещение внутри одной строки
			if (
				overData?.type === 'widget' &&
				overData.rowId === activeWidgetInfo.rowId
			) {
				const targetRow = rows.find(r => r.id === activeWidgetInfo.rowId)
				if (!targetRow) return

				const oldIndex = targetRow.widgets.findIndex(w => w.id === active.id)
				const newIndex = targetRow.widgets.findIndex(w => w.id === over.id)

				if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
					const reorderedWidgets = arrayMove(
						targetRow.widgets,
						oldIndex,
						newIndex,
					)
					const updatedRows = reorderWidgetsInRow(
						rows,
						activeWidgetInfo.rowId,
						reorderedWidgets,
					)
					onUpdateRows(updatedRows)
				}
			}
		},
		[rows, onUpdateRows, dispatch],
	)

	const activeWidget = useMemo(() => {
		if (!activeId) return null
		const widgetInfo = findWidgetInRows(activeId, rows)
		return widgetInfo?.widget || null
	}, [rows, activeId])

	return {
		sensors,
		handleDragStart,
		handleDragEnd,
		activeId,
		activeDockWidget,
		activeWidget,
	}
}
